import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { DollarSign, Send, Pencil, AlertTriangle, MessagesSquare } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { ParticleField } from "@/components/ParticleField";

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-500/15 text-amber-400",
    approved: "bg-emerald-500/15 text-emerald-400",
    completed: "bg-emerald-500/15 text-emerald-400",
    rejected: "bg-rose-500/15 text-rose-400",
    failed: "bg-rose-500/15 text-rose-400",
    paid: "bg-cyan-500/15 text-cyan-400",
  };
  return <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold capitalize ${map[status] || "bg-[#1a1a1a] text-zinc-400"}`}>{status}</span>;
}

export function PayoutsPage({ embedded = false } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("paypal");
  const [methodDetail, setMethodDetail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [fee, setFee] = useState(1.5);
  const [config, setConfig] = useState(null);
  const [hasPending, setHasPending] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);

  // Edit form state
  const [editMethod, setEditMethod] = useState("cashapp");
  const [editDetail, setEditDetail] = useState("");

  async function load() {
    setError(null);
    try {
      const res = await api.get("/payouts/history");
      const methodsRes = await api.get("/wallet/payment_methods");
      setData(res.data);
      setConfig(res.data?.config || null);
      setHasPending(!!res.data?.has_pending_payout);
      setFee(Number(res.data?.config?.platform_fee ?? res.data?.fee ?? 1.5));
      setPaymentMethods(methodsRes.data?.payment_methods || []);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  useEffect(() => { load(); }, []);

  const METHOD_OPTIONS = ["cashapp", "paypal", "crypto"];
  const METHOD_LABELS = {
    cashapp: "Cash App",
    paypal: "PayPal",
    crypto: "Crypto",
  };
  const METHOD_PLACEHOLDERS = {
    cashapp: "$YourCashtag",
    paypal: "you@email.com",
    crypto: "Solana Address",
  };
  const savedDetailFor = (type) => paymentMethods.find((pm) => pm.type === type)?.value || "";

  useEffect(() => {
    if (!requestOpen) return;
    const defaultMethod = paymentMethods[0]?.type || "cashapp";
    setMethod(defaultMethod);
    setMethodDetail(savedDetailFor(defaultMethod));
  }, [requestOpen, paymentMethods]);

  useEffect(() => {
    if (!editOpen) return;
    const defaultMethod = paymentMethods[0]?.type || "cashapp";
    setEditMethod(defaultMethod);
    setEditDetail(savedDetailFor(defaultMethod));
  }, [editOpen, paymentMethods]);

  async function savePaymentMethod() {
    const cleanedDetail = editDetail.trim();
    const nextMethods = paymentMethods.filter((pm) => pm.type !== editMethod);
    if (cleanedDetail) {
      nextMethods.push({
        type: editMethod,
        label: METHOD_LABELS[editMethod],
        value: cleanedDetail,
      });
    }
    try {
      await api.put("/wallet/payment_methods", { payment_methods: nextMethods });
      setPaymentMethods(nextMethods);
      toast({ title: "Saved", description: "Payment method updated" });
      setEditOpen(false);
    } catch (e) {
      toast({ title: "Error", description: apiErrorMessage(e) });
    }
  }

  async function requestPayout() {
    if (!amount || Number(amount) <= 0) return;
    const savedDetail = savedDetailFor(method);
    const finalDetail = methodDetail.trim() || savedDetail;
    if (!finalDetail) {
      toast({ title: "Missing payment detail", description: "Add your payout method details before requesting." });
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/payouts/request", { amount: Number(amount), method, method_detail: finalDetail || null });
      toast({ title: "Payout Requested", description: `$${(Number(amount) - feeForMethod(method)).toFixed(2)} estimated after fees` });
      setAmount(""); setMethodDetail("");
      setRequestOpen(false);
      await load();
    } catch (e) {
      toast({ title: "Error", description: typeof e?.response?.data?.detail === "string" ? e.response.data.detail : apiErrorMessage(e) });
    } finally { setSubmitting(false); }
  }

  const feeForMethod = (m) => {
    if (!config) return fee;
    const proc = Number(config.processors?.[m] ?? 0);
    return Number(config.platform_fee ?? 0) + proc;
  };

  const available = data?.available_balance ?? 0;
  const history = data?.items || [];
  const currentFee = feeForMethod(method);
  const netAmount = amount && Number(amount) > currentFee ? (Number(amount) - currentFee).toFixed(2) : "0.00";
  const minPayout = Number(config?.min_withdrawal ?? 10);
  const belowThreshold = Number(available) < minPayout;
  const navigate = useNavigate();

  return (
    <div data-testid="payouts-page" className="space-y-8">
      {!embedded && (
        <div>
          <h1 data-testid="payouts-title" className="page-title">Payouts</h1>
          <p className="page-subtitle">Manage your earnings and request withdrawals</p>
        </div>
      )}

      {error && <div className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{error}</div>}

      {/* Split Balance Hero — dark balance side / white action side */}
      <div
        data-testid="payout-balance-card"
        className="grid overflow-hidden rounded-[28px] border border-zinc-200 shadow-[0_20px_60px_-24px_rgba(15,23,42,0.18)] dark:border-white/[0.06] dark:shadow-[0_20px_60px_-12px_rgba(0,0,0,0.7)] lg:grid-cols-[1.55fr_1fr]"
      >
        {/* LEFT — Dark balance panel with floating particle motif */}
        <div className="relative isolate overflow-hidden bg-[#0a0a0a] p-7 sm:p-10 min-h-[260px] sm:min-h-[400px]">
          {/* Lime radial wash */}
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_75%_60%,rgba(200,255,0,0.32),transparent_55%)]" />

          {/* Floating particle animation (replaces 3D play-button image) */}
          <ParticleField color="#c8ff00" density={0.075} />

          <div className="relative">
            <div data-testid="payout-balance-label" className="text-[15px] font-medium text-white/85">Balance</div>
            <div
              data-testid="payout-available-amount"
              className="mt-2 text-5xl font-extrabold tracking-tight text-white tabular-nums sm:text-7xl"
              style={{ textShadow: "0 4px 24px rgba(0,0,0,0.35)" }}
            >
              ${Number(available).toFixed(2)}
            </div>
          </div>
        </div>

        {/* RIGHT — Action panel (darker on dark mode, light on light mode) */}
        <div className="flex flex-col gap-5 bg-white p-6 sm:p-8 dark:bg-[#0f0f0f]">
          <div
            data-testid="payout-threshold-notice"
            className={[
              "flex items-center gap-2 text-[13.5px] font-medium",
              belowThreshold ? "text-amber-700 dark:text-amber-400" : "text-emerald-700 dark:text-emerald-400",
            ].join(" ")}
          >
            <AlertTriangle size={15} className={belowThreshold ? "text-amber-500" : "text-emerald-500"} />
            {belowThreshold ? `$${minPayout} Required for Payout` : "Ready to withdraw"}
          </div>

          <div className="flex flex-1 flex-col justify-center gap-3">
            <button
              data-testid="payout-request-btn"
              disabled={belowThreshold}
              onClick={() => { setAmount(String(available || "")); setRequestOpen(true); }}
              className={[
                "group inline-flex w-full items-center justify-center gap-2.5 rounded-[18px] px-5 py-4 text-[15px] font-semibold transition-all duration-200 active:scale-[0.98]",
                belowThreshold
                  ? "cursor-not-allowed bg-zinc-400 text-white dark:bg-zinc-700 dark:text-zinc-400"
                  : "bg-[#c8ff00] text-black hover:bg-[#b6ee00] shadow-[0_8px_24px_-8px_rgba(200,255,0,0.55)]",
              ].join(" ")}
            >
              <DollarSign size={16} strokeWidth={2.4} /> Request Payout
            </button>

            <button
              data-testid="payout-edit-btn"
              onClick={() => setEditOpen(true)}
              disabled={hasPending}
              className={[
                "inline-flex w-full items-center justify-center gap-2.5 rounded-[18px] px-5 py-4 text-[15px] font-semibold transition-all duration-200 active:scale-[0.98]",
                hasPending
                  ? "cursor-not-allowed bg-zinc-300 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-500"
                  : "bg-black text-white hover:bg-zinc-800 dark:bg-white dark:text-black dark:hover:bg-zinc-200",
              ].join(" ")}
            >
              <Pencil size={15} strokeWidth={2.2} /> Edit
            </button>
            {hasPending && (
              <p data-testid="payout-edit-locked-note" className="text-center text-[11.5px] font-medium text-amber-600 dark:text-amber-400">
                Payout details are locked while a withdrawal is pending.
              </p>
            )}
          </div>

          <a
            href="https://discord.gg/8rhhsY8aHb"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="payout-discord-help"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-zinc-600 transition-colors hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
          >
            <MessagesSquare size={14} className="text-[#5865f2]" />
            <span className="underline-offset-2 hover:underline">Need Help? Join Discord community for guidance!</span>
          </a>
        </div>
      </div>

      {/* Payout History */}
      <div data-testid="payout-history" className="space-y-3">
        <h2 data-testid="payout-history-title" className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-white sm:text-3xl">Payout History</h2>

        {history.length === 0 ? (
          <div className="space-y-5">
            <p
              data-testid="payout-history-empty"
              className="text-[14px] text-zinc-500 dark:text-zinc-400"
            >
              No payouts history yet. Join campaigns now to start earning!
            </p>
            <button
              data-testid="payout-browse-campaigns-btn"
              onClick={() => navigate("/campaigns")}
              className="inline-flex items-center gap-2 rounded-[18px] bg-white px-5 py-3 text-[14px] font-semibold text-zinc-900 shadow-[0_10px_24px_-12px_rgba(15,23,42,0.25)] ring-1 ring-inset ring-zinc-200 transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_28px_-12px_rgba(15,23,42,0.32)] active:scale-[0.98] dark:bg-[#0a0a0a] dark:text-white dark:ring-white/[0.08] dark:shadow-[0_10px_24px_-12px_rgba(0,0,0,0.7)]"
            >
              Browse Campaigns
            </button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white dark:border-white/[0.06] dark:bg-[#0a0a0a]">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 text-left dark:border-white/[0.06]">
                    <th className="px-4 py-3 text-[10px] font-medium uppercase tracking-widest text-zinc-500">Date</th>
                    <th className="px-4 py-3 text-[10px] font-medium uppercase tracking-widest text-zinc-500">Payment Method</th>
                    <th className="px-4 py-3 text-right text-[10px] font-medium uppercase tracking-widest text-zinc-500">Amount</th>
                    <th className="px-4 py-3 text-right text-[10px] font-medium uppercase tracking-widest text-zinc-500">Fee</th>
                    <th className="px-4 py-3 text-right text-[10px] font-medium uppercase tracking-widest text-zinc-500">Amount Paid</th>
                    <th className="px-4 py-3 text-[10px] font-medium uppercase tracking-widest text-zinc-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((p) => (
                    <tr key={p.id} data-testid={`payout-row-${p.id}`} className="border-b border-zinc-100 last:border-0 dark:border-white/[0.04]">
                      <td className="px-4 py-3 text-xs text-zinc-600 dark:text-zinc-400">{new Date(p.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</td>
                      <td className="px-4 py-3 text-xs font-medium capitalize text-zinc-900 dark:text-white">{p.method}</td>
                      <td className="px-4 py-3 text-right text-xs font-medium tabular-nums text-zinc-900 dark:text-white">-${Number(p.amount).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-xs tabular-nums text-zinc-500">${Number(p.fee).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-xs font-bold tabular-nums text-[#7a9500] dark:text-[#c8ff00]">${Number(p.amount_paid).toFixed(2)}</td>
                      <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Edit Payment Methods Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md bg-[#0a0a0a] border-[#1a1a1a] text-white">
          <DialogHeader>
            <DialogTitle>Payment Methods</DialogTitle>
            <DialogDescription data-testid="payout-methods-dialog-description">Update where approved payout requests should be sent.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-4">
              <div className="grid gap-2">
                <label className="text-xs uppercase tracking-widest text-zinc-500">Payment Method</label>
                <Select value={editMethod} onValueChange={(value) => { setEditMethod(value); setEditDetail(savedDetailFor(value)); }}>
                  <SelectTrigger data-testid="payout-edit-method" className="rounded-lg border-[#1a1a1a] bg-black text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {METHOD_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>{METHOD_LABELS[option]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <label className="text-xs uppercase tracking-widest text-zinc-500">{METHOD_LABELS[editMethod]} Details</label>
                <Input
                  data-testid="payout-edit-detail"
                  value={editDetail}
                  onChange={(e) => setEditDetail(e.target.value)}
                  placeholder={METHOD_PLACEHOLDERS[editMethod]}
                  className="rounded-lg border-[#1a1a1a] bg-[#0a0a0a] text-white text-sm placeholder:text-zinc-600"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="payout-methods-save" className="w-full rounded-xl bg-[#c8ff00] text-black font-bold hover:bg-[#b8ef00]" onClick={savePaymentMethod}>
                Save Changes
              </Button>
              <Button data-testid="payout-methods-cancel" variant="ghost" className="w-full rounded-xl border border-[#1a1a1a]" onClick={() => setEditOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent data-testid="payout-request-dialog" className="max-w-lg bg-[#0a0a0a] border-[#1a1a1a] text-white">
          <DialogHeader><DialogTitle>Request Payout</DialogTitle><DialogDescription data-testid="payout-request-dialog-description">Review the payout amount, fee, and destination before submitting.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-[10px] font-medium uppercase tracking-wider text-zinc-600">Amount ($) — editable</label>
                <Input
                  data-testid="payout-amount"
                  type="number"
                  value={amount}
                  min={0}
                  max={available}
                  step="0.01"
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="rounded-lg border-[#1a1a1a] bg-black text-white text-lg font-bold"
                />
                <div className="mt-1 flex items-center justify-between text-[10.5px] text-zinc-500">
                  <span>Available <span className="font-bold text-[#c8ff00]">${Number(available).toFixed(2)}</span></span>
                  <button
                    type="button"
                    data-testid="payout-amount-max"
                    onClick={() => setAmount(String(available))}
                    className="font-semibold text-[#c8ff00] underline-offset-2 hover:underline"
                  >
                    Use Max
                  </button>
                </div>
              </div>
              <div><label className="mb-1.5 block text-[10px] font-medium uppercase tracking-wider text-zinc-600">Payment Method</label><Select value={method} onValueChange={(value) => { setMethod(value); setMethodDetail(savedDetailFor(value)); }}><SelectTrigger data-testid="payout-method" className="rounded-lg border-[#1a1a1a] bg-black text-white"><SelectValue /></SelectTrigger><SelectContent>{METHOD_OPTIONS.map((option) => (<SelectItem key={option} value={option}>{METHOD_LABELS[option]}</SelectItem>))}</SelectContent></Select></div>
            </div>
            <div><label className="mb-1.5 block text-[10px] font-medium uppercase tracking-wider text-zinc-600">{METHOD_LABELS[method]} Details</label><Input data-testid="payout-detail" value={methodDetail} onChange={(e) => setMethodDetail(e.target.value)} placeholder={METHOD_PLACEHOLDERS[method]} className="rounded-lg border-[#1a1a1a] bg-black text-white" /></div>
            <div data-testid="payout-breakdown" className="rounded-xl border border-[#c8ff00]/20 bg-[#c8ff00]/[0.03] p-4">
              <div className="text-sm font-semibold text-white">Payout History & Status Preview</div>
              <div className="mt-3 grid grid-cols-3 gap-3 text-xs"><div><div className="text-zinc-600">Amount</div><div className="font-bold text-white">${Number(amount || 0).toFixed(2)}</div></div><div><div className="text-zinc-600">Total Fee</div><div className="font-bold text-white">${Number(currentFee).toFixed(2)}</div></div><div><div className="text-zinc-600">Amount Paid</div><div className="font-black text-[#c8ff00]">${netAmount}</div></div></div>
              <div className="mt-2 text-[11px] text-zinc-500">Processor ${Number(config?.processors?.[method] ?? 0).toFixed(2)} + Platform ${Number(config?.platform_fee ?? 0).toFixed(2)}. Minimum withdrawal ${minPayout.toFixed(2)}.</div>
              <div className="mt-3 text-xs text-zinc-500">Status will display as <span className="text-white">Pending</span> after submission.</div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="payout-request-cancel" variant="ghost" className="w-full rounded-xl border border-[#1a1a1a]" onClick={() => setRequestOpen(false)}>
                Close
              </Button>
              <Button data-testid="payout-submit" className="w-full gap-1.5 rounded-xl bg-[#c8ff00] text-black font-bold hover:bg-[#b8ef00] active:scale-[0.97]" onClick={requestPayout} disabled={submitting || !amount || Number(amount) < minPayout || Number(amount) <= currentFee || !methodDetail.trim()}><Send size={14} /> {submitting ? "Submitting..." : "Request Payout"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
